import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Send, Bot, User, Loader2, Plus, MessageSquare, Zap, HelpCircle, RefreshCw, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachPage() {
  const { user, checkAuth } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [personality, setPersonality] = useState(user?.coach_personality || 'direct');
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [openingQuestion, setOpeningQuestion] = useState(null);
  const [loadingOpener, setLoadingOpener] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchSessions();
    fetchHistory();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSessions = async () => {
    try {
      const { data } = await axios.get(`${API}/coach/sessions`, { withCredentials: true });
      setSessions(data);
    } catch (e) { console.error(e); }
  };

  const fetchHistory = async (sid) => {
    setLoadingHistory(true);
    setOpeningQuestion(null);
    try {
      const params = sid ? `?session_id=${sid}` : '';
      const { data } = await axios.get(`${API}/coach/history${params}`, { withCredentials: true });
      setMessages(data.messages || []);
      setSessionId(data.session_id);
      // If no messages, fetch an opening question
      if (!data.messages || data.messages.length === 0) {
        fetchOpeningQuestion();
      }
    } catch (e) { console.error(e); }
    finally { setLoadingHistory(false); }
  };

  const fetchOpeningQuestion = async () => {
    setLoadingOpener(true);
    try {
      const { data } = await axios.post(`${API}/coach/open`, {}, { withCredentials: true, timeout: 60000 });
      setOpeningQuestion(data);
    } catch (e) { console.error(e); }
    finally { setLoadingOpener(false); }
  };

  const handleNewSession = () => {
    const newSid = `coach-${user?.id}-${Date.now()}`;
    setSessionId(newSid);
    setMessages([]);
    setOpeningQuestion(null);
    fetchOpeningQuestion();
    inputRef.current?.focus();
  };

  const handlePersonalityChange = async (newPersonality) => {
    setPersonality(newPersonality);
    try {
      await axios.put(`${API}/profile`, { coach_personality: newPersonality }, { withCredentials: true });
      await checkAuth();
    } catch (e) { console.error(e); }
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg, created_at: new Date().toISOString() }]);
    setSending(true);

    try {
      const { data } = await axios.post(`${API}/coach/chat`, {
        message: userMsg,
        session_id: sessionId
      }, { withCredentials: true, timeout: 120000 });

      setMessages(prev => [...prev, { role: 'assistant', content: data.response, created_at: new Date().toISOString() }]);
      setSessionId(data.session_id);
      fetchSessions();
    } catch (e) {
      const errorDetail = e.response?.data?.detail || '';
      const errorMsg = errorDetail.includes('temporarily unavailable')
        ? 'The AI coach is temporarily unavailable. Please click retry or try again in a moment.'
        : 'I encountered a connection issue. Please try sending your message again.';
      setMessages(prev => [...prev, { role: 'error', content: errorMsg, failed_message: userMsg, created_at: new Date().toISOString() }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleRetry = (failedMessage) => {
    // Remove the error message and resend
    setMessages(prev => prev.filter(m => m.role !== 'error' || m.failed_message !== failedMessage));
    setInput(failedMessage);
    setTimeout(() => { handleSend(); }, 100);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div data-testid="coach-page" className="flex h-screen">
      {/* Sessions Sidebar */}
      <div className="w-64 bg-[#0A0A0A] border-r border-[#262626] flex flex-col shrink-0">
        <div className="p-4 border-b border-[#262626]">
          <button
            data-testid="new-session-button"
            onClick={handleNewSession}
            className="w-full bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold py-2.5 px-4 rounded-sm transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" /> New Session
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.map((s) => (
            <button
              key={s.session_id}
              data-testid={`session-${s.session_id}`}
              onClick={() => fetchHistory(s.session_id)}
              className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-all flex items-center gap-2
                ${sessionId === s.session_id ? 'bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30' : 'text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A]'}`}
            >
              <MessageSquare className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{s.message_count} messages</span>
            </button>
          ))}
          {sessions.length === 0 && (
            <p className="text-[#737373] text-xs text-center py-4">No sessions yet</p>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header with personality toggle */}
        <div className="h-16 bg-[#0A0A0A] border-b border-[#262626] flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#FF3B30]/10 rounded-full flex items-center justify-center">
              <Bot className="w-4 h-4 text-[#FF3B30]" />
            </div>
            <div>
              <p className="text-white text-sm font-semibold">AI Coach</p>
              <p className="text-[#737373] text-xs">Executive Presence & Resilience</p>
            </div>
          </div>
          {/* Coach Personality Toggle */}
          <div data-testid="personality-toggle" className="flex items-center gap-2 bg-[#141414] border border-[#262626] rounded-sm p-1">
            <button
              data-testid="personality-direct"
              onClick={() => handlePersonalityChange('direct')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-bold transition-all
                ${personality === 'direct'
                  ? 'bg-[#FF3B30] text-white'
                  : 'text-[#A3A3A3] hover:text-white'
                }`}
            >
              <Zap className="w-3 h-3" /> Direct
            </button>
            <button
              data-testid="personality-curious"
              onClick={() => handlePersonalityChange('curious')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-bold transition-all
                ${personality === 'curious'
                  ? 'bg-[#FF3B30] text-white'
                  : 'text-[#A3A3A3] hover:text-white'
                }`}
            >
              <HelpCircle className="w-3 h-3" /> Curious
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loadingHistory ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-lg">
                <div className="w-16 h-16 bg-[#FF3B30]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-[#FF3B30]" />
                </div>
                
                {loadingOpener ? (
                  <>
                    <h3 className="text-white font-semibold text-lg mb-2">Preparing your session...</h3>
                    <div className="flex items-center justify-center gap-2 text-[#A3A3A3] text-sm">
                      <Loader2 className="w-4 h-4 animate-spin text-[#FF3B30]" />
                      <span>Your coach is crafting an opening question for this week's module</span>
                    </div>
                  </>
                ) : openingQuestion ? (
                  <>
                    {openingQuestion.module && (
                      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-3">
                        Week {openingQuestion.module.week}: {openingQuestion.module.title}
                      </p>
                    )}
                    <div data-testid="opening-question" className="bg-[#141414] border border-[#FF3B30]/30 rounded-sm p-5 mb-6 text-left">
                      <div className="flex items-start gap-3">
                        <Bot className="w-5 h-5 text-[#FF3B30] shrink-0 mt-0.5" />
                        <p className="text-white text-sm leading-relaxed">{openingQuestion.question}</p>
                      </div>
                    </div>
                    <p className="text-[#737373] text-xs mb-4">Respond to the question above, or ask your own:</p>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        "I'd like to explore that question",
                        "I have something else on my mind today",
                        "Help me with this week's exercise",
                      ].map((q) => (
                        <button
                          key={q}
                          onClick={() => { setInput(q); inputRef.current?.focus(); }}
                          className="text-left bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 text-[#A3A3A3] text-sm hover:border-[#FF3B30]/50 hover:text-white transition-all"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="text-white font-semibold text-lg mb-2">Your AI Coach is Ready</h3>
                    <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6">
                      Start a conversation about this week's module, leadership challenges, or your growth journey.
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            messages.filter(m => m.role !== 'error' || true).map((msg, i) => (
              msg.role === 'error' ? (
                <div key={i} data-testid={`chat-error-${i}`} className="flex gap-3 justify-start">
                  <div className="w-7 h-7 bg-[#F59E0B]/10 rounded-full flex items-center justify-center shrink-0 mt-1">
                    <AlertCircle className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </div>
                  <div className="max-w-[70%] rounded-sm p-4 text-sm leading-relaxed bg-[#F59E0B]/5 border border-[#F59E0B]/30 text-[#F59E0B]">
                    <p>{msg.content}</p>
                    {msg.failed_message && (
                      <button
                        data-testid="retry-button"
                        onClick={() => handleRetry(msg.failed_message)}
                        className="mt-2 flex items-center gap-1.5 text-xs font-bold bg-[#F59E0B] text-black px-3 py-1.5 rounded-sm hover:bg-[#D97706] transition-colors"
                      >
                        <RefreshCw className="w-3 h-3" /> Retry
                      </button>
                    )}
                  </div>
                </div>
              ) : (
              <div
                key={i}
                data-testid={`chat-message-${i}`}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 bg-[#FF3B30]/10 rounded-full flex items-center justify-center shrink-0 mt-1">
                    <Bot className="w-3.5 h-3.5 text-[#FF3B30]" />
                  </div>
                )}
                <div className={`max-w-[70%] rounded-sm p-4 text-sm leading-relaxed
                  ${msg.role === 'user'
                    ? 'bg-[#FF3B30] text-white'
                    : 'bg-[#141414] border border-[#262626] text-[#E5E5E5]'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm prose-invert max-w-none [&_p]:mb-2 [&_ul]:mb-2 [&_ol]:mb-2 [&_li]:text-[#E5E5E5] [&_strong]:text-white [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : msg.content}
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 bg-[#262626] rounded-full flex items-center justify-center shrink-0 mt-1">
                    <User className="w-3.5 h-3.5 text-[#A3A3A3]" />
                  </div>
                )}
              </div>
              )
            ))
          )}
          {sending && (
            <div className="flex gap-3">
              <div className="w-7 h-7 bg-[#FF3B30]/10 rounded-full flex items-center justify-center shrink-0 mt-1">
                <Bot className="w-3.5 h-3.5 text-[#FF3B30]" />
              </div>
              <div className="bg-[#141414] border border-[#262626] rounded-sm p-4 flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-[#FF3B30] animate-spin" />
                <span className="text-[#737373] text-sm">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-[#262626] p-4 bg-[#0A0A0A]">
          <div className="flex items-end gap-3 max-w-4xl mx-auto">
            <textarea
              ref={inputRef}
              data-testid="coach-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask your coach anything..."
              rows={1}
              className="flex-1 bg-[#141414] border border-[#262626] rounded-sm px-4 py-3 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] focus:border-[#FF3B30] transition-all resize-none text-sm"
            />
            <button
              data-testid="coach-send-button"
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="bg-[#FF3B30] hover:bg-[#D62828] text-white p-3 rounded-sm transition-colors disabled:opacity-50 shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
